/**
 * Project Tracker Utility
 * 
 * Keeps track of the project's development progress, including:
 * - Components created
 * - Pages implemented
 * - Tests written
 * - Overall project status
 */

interface TrackerItem {
  name: string;
  status: 'completed' | 'in-progress' | 'planned';
  notes?: string;
}

interface ProjectStatus {
  components: TrackerItem[];
  pages: TrackerItem[];
  tests: TrackerItem[];
  projectProgress: number; // 0-100
}

class ProjectTracker {
  private static status: ProjectStatus = {
    components: [],
    pages: [],
    tests: [],
    projectProgress: 0
  };

  static initialize(): void {
    // Initialize with current status
    this.status = {
      components: [
        { name: 'Layout', status: 'completed' },
        { name: 'Navbar', status: 'completed' },
        { name: 'Footer', status: 'completed' },
        { name: 'ServiceCard', status: 'completed' },
        { name: 'Button', status: 'completed' },
        { name: 'Hero', status: 'completed' },
      ],
      pages: [
        { name: 'Home', status: 'completed' },
        { name: 'Services', status: 'planned' },
        { name: 'About', status: 'planned' },
        { name: 'Reservation', status: 'planned' },
        { name: 'Contact', status: 'planned' },
        { name: 'Legal', status: 'planned' },
      ],
      tests: [
        { name: 'Navbar.test.tsx', status: 'completed' },
        { name: 'Footer.test.tsx', status: 'completed' },
        { name: 'ServiceCard.test.tsx', status: 'completed' },
        { name: 'Button.test.tsx', status: 'completed' },
        { name: 'Hero.test.tsx', status: 'completed' },
      ],
      projectProgress: 25 // Initial progress percentage
    };

    this.logStatus();
  }

  static updateComponentStatus(name: string, status: 'completed' | 'in-progress' | 'planned', notes?: string): void {
    const componentIndex = this.status.components.findIndex(comp => comp.name === name);
    
    if (componentIndex >= 0) {
      this.status.components[componentIndex] = { name, status, notes };
    } else {
      this.status.components.push({ name, status, notes });
    }
    
    this.calculateProgress();
    this.logStatus();
  }

  static updatePageStatus(name: string, status: 'completed' | 'in-progress' | 'planned', notes?: string): void {
    const pageIndex = this.status.pages.findIndex(page => page.name === name);
    
    if (pageIndex >= 0) {
      this.status.pages[pageIndex] = { name, status, notes };
    } else {
      this.status.pages.push({ name, status, notes });
    }
    
    this.calculateProgress();
    this.logStatus();
  }

  static updateTestStatus(name: string, status: 'completed' | 'in-progress' | 'planned', notes?: string): void {
    const testIndex = this.status.tests.findIndex(test => test.name === name);
    
    if (testIndex >= 0) {
      this.status.tests[testIndex] = { name, status, notes };
    } else {
      this.status.tests.push({ name, status, notes });
    }
    
    this.calculateProgress();
    this.logStatus();
  }

  private static calculateProgress(): void {
    const totalItems = this.status.components.length + this.status.pages.length + this.status.tests.length;
    const completedItems = 
      this.status.components.filter(item => item.status === 'completed').length +
      this.status.pages.filter(item => item.status === 'completed').length +
      this.status.tests.filter(item => item.status === 'completed').length;
    
    this.status.projectProgress = Math.round((completedItems / totalItems) * 100);
  }

  static getStatus(): ProjectStatus {
    return this.status;
  }

  private static logStatus(): void {
    console.log('Project Status:', this.status);
    console.log(`Overall Progress: ${this.status.projectProgress}%`);
  }
}

export default ProjectTracker;